import { useEffect, useState } from "react"
import { useAppSelector } from "@/store/hooks"
import { startTimerTickSound, stopTimerTickSound } from "@/utils/audioManager"
import { GameTimerKey, useGameTimersStore } from "@/utils/gameTimers"

/**
 * Ticks the clock while the local player is on a timer — their turn, an auction,
 * a card decision, the decision hub, role picking, a trade they have to answer.
 *
 * Several of those overlap (the turn timer keeps running underneath a decision
 * modal), so the loop is shared: every mounted caller holds a claim on it, and
 * it only stops once the last claim is dropped.
 */
let claims = 0
let pendingStop: number | null = null

// Handing over from one timer to the next unmounts the old caller before the new
// one mounts, which would stop and immediately restart the loop. Stopping is
// deferred by a beat so that hand-over is inaudible.
const HANDOVER_GRACE_MS = 250

// How long a countdown may sit on the same number before we call it paused.
// The server pauses a timer by clearing its interval (timerHelpers.pauseTimer)
// and tells nobody — the value simply stops arriving — so a stall is the only
// evidence the client gets. Set above one second of jitter, since every running
// timer sends a new value every second.
const STALL_MS = 2200

const claimTick = () => {
  if (pendingStop !== null) {
    clearTimeout(pendingStop)
    pendingStop = null
  }
  claims += 1
  if (claims === 1) startTimerTickSound()
}

const releaseTick = () => {
  claims = Math.max(0, claims - 1)
  if (claims > 0 || pendingStop !== null) return
  pendingStop = window.setTimeout(() => {
    pendingStop = null
    if (claims === 0) stopTimerTickSound()
  }, HANDOVER_GRACE_MS)
}

/**
 * @param active   whether this timer is one the local player is on right now
 * @param timerKey the countdown this caller is ticking for. Naming it makes the
 *                 sound follow the clock rather than the modal: it only ticks
 *                 while that clock is above zero and still moving, so a turn
 *                 timer frozen for a decision, property management open, or a
 *                 paused game goes quiet, and picks back up on the next value
 *                 that arrives.
 *
 * The clock is watched through a subscription rather than a prop, so callers do
 * not have to hold the seconds in a selector — holding them is what used to
 * re-render Board (and every modal on screen) once a second. What is kept in
 * state here is only whether the clock is alive, which changes on the edges.
 */
export const useTimerTickSound = (active: boolean, timerKey?: GameTimerKey) => {
  // A locally paused game freezes the timers too, and this one we hear about
  // immediately rather than having to wait out a stall.
  const isGamePaused = useAppSelector((state) => state.global.isGamePaused)
  // Animations the clock has to stay out of the way of: the dice in the air, a
  // token travelling the board, a card being drawn or played. Each owns its
  // moment and its own sound, and the countdown underneath is frozen for the
  // length of it — so this is the same silence a stall would give us, only
  // without the 2 second wait.
  const pieceMoving = useAppSelector(
    (state) => state.global.pieceMovementAnimationRunning
  )
  const cardAnimationsRunning = useAppSelector(
    (state) => state.global.actionCardAnimationRunning
  )
  // Set from your own roll (the button, or the idle auto-roll) and cleared by
  // the server once the dice have landed, which runs straight into the piece
  // movement above — so roll through to arrival is one unbroken silence.
  const diceRolling = useAppSelector((state) => state.global.diceRolling)
  const animating = !!pieceMoving || cardAnimationsRunning > 0 || diceRolling
  const timers = useGameTimersStore()

  // Callers that name no clock opt out of both checks rather than being muted.
  const [clockRunning, setClockRunning] = useState(true)

  useEffect(() => {
    if (!active || !timerKey) {
      setClockRunning(true)
      return
    }

    let watchdog: number | undefined
    let last = timers.getTimers()[timerKey]

    // Re-read the clock and re-arm the stall watchdog. Only ever fires the
    // "gone quiet" flag once a countdown has genuinely stopped arriving.
    const settle = () => {
      const seconds = timers.getTimers()[timerKey]
      setClockRunning(seconds !== undefined && seconds > 0)
      window.clearTimeout(watchdog)
      watchdog = window.setTimeout(() => setClockRunning(false), STALL_MS)
    }

    settle()
    const unsubscribe = timers.subscribe(() => {
      const seconds = timers.getTimers()[timerKey]
      if (seconds === last) return
      last = seconds
      settle()
    })

    return () => {
      window.clearTimeout(watchdog)
      unsubscribe()
    }
  }, [active, timerKey, timers])

  const shouldTick = active && clockRunning && !isGamePaused && !animating

  useEffect(() => {
    if (!shouldTick) return
    claimTick()
    return releaseTick
  }, [shouldTick])
}
